import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { initGoogleAuth, isGoogleAuthConfigured } from '../utils/googleAuth';

const SESSION_KEY = 'onetap_dismissed';

/**
 * Shows Google One Tap prompt (the small popup in top-right corner)
 * on any page where the user is not authenticated.
 */
export const useGoogleOneTap = () => {
  const { isAuthenticated, isInitializing, loginWithGoogle } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isGoogleAuthConfigured() || isAuthenticated || isInitializing) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;

    const showPrompt = () => {
      window.google!.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID!,
        callback: async (response: { credential: string }) => {
          try {
            const result = await loginWithGoogle(response.credential);
            navigate(result.needsOnboarding ? '/app/subscription' : '/app/dashboard');
          } catch {
            // silently ignore – user can still sign in manually
          }
        },
        cancel_on_tap_outside: true,
      });
      window.google!.accounts.id.prompt((notification: { isSkippedMoment: () => boolean; isDismissedMoment: () => boolean }) => {
        if (notification.isSkippedMoment() || notification.isDismissedMoment()) {
          sessionStorage.setItem(SESSION_KEY, '1');
        }
      });
    };

    if (window.google?.accounts?.id) {
      showPrompt();
    } else {
      initGoogleAuth().then(showPrompt).catch(() => {});
    }

    return () => {
      window.google?.accounts?.id.cancel?.();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isInitializing]);
};
